import { FormFieldRow } from "@/Common/Components/Forms/FormFieldRow";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { ValueSetType } from "../Constants/ValueSetType";
import { CodeValue } from "../Models/CodeValue";
import { ImmunizationModel } from "../Models/ImmunizationModel";
import { LocationModel } from "../Models/LocationModel";
import { MedicationBatchModel } from "../Models/MedicalBatchModel";
import { PractitionerModel } from "../Models/PractitionerModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { BaseImmunizationProfileSection, BaseImmunizationProfileSectionState, IBaseImmunizationProfileSectionProps } from "./BaseImmunizationProfileSection";

interface ViewImmunizationProp extends IBaseImmunizationProfileSectionProps {
    immunization: ImmunizationModel;
    medicalImmunizatioModel: MedicalItemImmunization;
    modifyImmunization: () => void;
}

class ViewImmunizationState extends BaseImmunizationProfileSectionState {
    public immunization: ImmunizationModel;
    public batches: MedicationBatchModel[];

    constructor(init?: Partial<ViewImmunizationState>) {
        super(init);
    }

    public static default(): ViewImmunizationState {
        return new ViewImmunizationState({
            ...BaseImmunizationProfileSectionState.default(),
            immunization: null,
            batches: []
        });
    }
}

class ViewImmunization extends BaseImmunizationProfileSection<ViewImmunizationState, ViewImmunizationProp> {
    protected requestValues = null;

    public constructor(props: ViewImmunizationProp) {
        super(props);

        const initialState = ViewImmunizationState.default();
        initialState.immunization = new ImmunizationModel(props.immunization);

        this.state = initialState;
    }

    public componentDidMount(): void {
        this.loadMedicationBatches();
    }

    protected getBody(): JSX.Element {
        const immunization = this.state.immunization;

        return (
            <div className="col-md-12" style={{ maxHeight: "700px", overflowY: "auto" }}>
                <div className="row margin-sm-bottom">
                    {this.renderRow("QuebecVaccinationRegistry_CommercialNameLabel", immunization.VaccineTradeName)}
                    {this.renderRow("QuebecVaccinationRegistry_ImmunizingAgentLabel", QuebecVaccinationRegistryHelper.translateValueSetString(immunization.Vaccine, ValueSetType.Agent))}
                    {immunization.ReasonsAdministered?.any() &&
                        this.renderRow("QuebecVaccinationRegistry_AdministrationReasonLabel", this.getValueToShow(immunization.ReasonsAdministered[0], ValueSetType.AdministrationReason))
                    }
                    {this.renderRow("AdministeredDateDLabel", DateHelper.formatDate(immunization.Date, "YYYY/MM/DD"))}
                    {this.renderRow("LotNumberDLabel", this.getLotNumber())}
                    {this.renderRow("QuebecVaccinationRegistry_QuantityAdministeredLabel", this.getDoseString())}
                    {this.renderRow("RouteDLabel", this.getValueToShow(immunization.VaccineRoute, ValueSetType.AdministrationRoute))}
                    {this.renderRow("QuebecVaccinationRegistry_AdministrationSiteLabel", this.getValueToShow(immunization.VaccineSite, ValueSetType.AdministrationSite))}
                    {this.renderRow("VaccinatorLabel", PractitionerModel.getDisplayName(immunization.AdministeredBy))}
                    {this.renderRow("StatusDLabel", QuebecVaccinationRegistryHelper.translateValueSetString(immunization.OverrideStatus, "ImmunizationHistoryStatus"))}
                    {this.renderRow("QuebecVaccinationRegistry_ImmunizationLocationLabel", LocationModel.getDisplayName(immunization.Location))}
                    {this.renderNotes()}
                </div>
            </div>
        );
    }

    private getValueToShow(code: CodeValue, setType: ValueSetType): string {
        return QuebecVaccinationRegistryHelper.translateValueSetString(code, setType);
    }

    private renderRow(labelKey: string, element: string): JSX.Element {
        return (
            <FormFieldRow
                labelValueKey={labelKey}
                isEditMode={false}
                value={element}
                hasParentClassName
            />
        );
    }

    protected async onSave(): Promise<void> {
        this.props.onClose();
        this.props.modifyImmunization();
    }

    private getDoseString(): string {
        const doseSize = this.state.immunization?.DoseSize?.toString();
        const doseUnit = this.getValueToShow(this.state.immunization?.DoseUnit, ValueSetType.DosageUnit);
        return !doseSize && !doseUnit
            ? "--"
            : `${doseSize ?? "--"} ${QuebecVaccinationRegistryHelper.translateValueSetString(this.state.immunization.DoseUnit, ValueSetType.DosageUnit) || "--"}`;
    }

    private async loadMedicationBatches(): Promise<void> {
        const result = await QuebecVaccinationRegistryHelper.getBatchesForMedication(this.state.immunization.VaccineTradeName);
        this.mutateState(s => s.batches = result.batches);
    }

    private renderNotes(): JSX.Element {
        // get grouped notes
        // The RVQ adds a separate automatic note for some operations, which is grouped under
        // the same author/date in our view.
        const notes = (this.state.immunization.Notes || [])
            .groupBy(p => p.Author + p.Date)
            .map(group => ({ Author: group[0].Author, Date: group[0].Date, Lines: group.map(p => p.Text) }))
            .sortByProperty(p => p.Date);

        // render view
        return (
            <FormFieldRow
                labelValueKey="CommentsLabel"
                isEditMode
                hasParentClassName
            >
                {(!notes.any())
                    ? <div>--</div>
                    : (
                        <div className="rvq-commentlist">
                            <ul>
                                {notes.map(note => (
                                    <li key={note.Author + note.Date}>
                                        <div style={{ display: "inline-block" }}>
                                            <div>{note.Author} ({DateHelper.formatDate(note.Date, "YYYY-MM-DD HH:mm:ss")})</div>
                                            {note.Lines.map((line, i) => (
                                                <div key={line + i} style={{ marginLeft: "10px" }}>
                                                    {JSXHelper.BreakStringIntoParagraphs(line)}
                                                </div>
                                            ))}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )
                }
            </FormFieldRow>
        );
    }

    protected get isEditing(): boolean {
        return this.props.medicalImmunizatioModel?.QuebecRegistryId === this.state.immunization.Id;
    }

    protected setModifyButtonLabel(): string {
        return "ModifyLabel";
    }

    private getLotNumber(): string {
        const immunization = this.state.immunization;
        const currentBatch = this.state.batches?.firstOrDefault(batch => batch.Id === immunization.LotId);

        return MedicationBatchModel.getLotDisplayName(currentBatch) || "--";
    }
}

export { ViewImmunization };
